import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Bell, FileBarChart, Settings, LogOut, ShieldCheck, BarChart3, Handshake, Sun, Moon, Route, LifeBuoy, Wrench, History, Monitor, Smartphone, SlidersHorizontal, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useViewMode } from "@/context/ViewModeContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import AssistantWidget from "@/components/AssistantWidget";
import MobileTabBar from "@/components/MobileTabBar";
import ChangePasswordForcedModal from "@/components/ChangePasswordForcedModal";
import logo from "@/assets/boomerang_logo.png";

const ASSISTANT_ENABLED = false; // Assistant IA désactivé temporairement (remettre true pour réactiver)

const nav = [
  { to: "/dashboard", label: "Live Tracking", icon: LayoutDashboard, testid: "nav-dashboard", feature: "map" },
  { to: "/analytique", label: "Tableau de bord", icon: BarChart3, testid: "nav-analytics", feature: "reports" },
  { to: "/historique", label: "Historique", icon: History, testid: "nav-history", feature: "history" },
  { to: "/alertes", label: "Alertes", icon: Bell, testid: "nav-alerts", feature: "history" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/rapports", label: "Rapports", icon: FileBarChart, testid: "nav-reports", feature: "reports" },
  { to: "/rapports/vitesse", label: "Vitesse & conduite", icon: Gauge, testid: "nav-speed-driving", feature: "reports" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets", feature: "tickets" },
  { to: "/interventions", label: "Interventions", icon: Wrench, testid: "nav-interventions", feature: "interventions", viewRoles: ["client"] },
  { to: "/trajets-types", label: "Trajets types", icon: Route, testid: "nav-route-templates", feature: "reports" },
  { to: "/admin", label: "Administration", icon: Settings, testid: "nav-admin", feature: "admin" },
  { to: "/partenaire", label: "Admin Partenaire", icon: Handshake, testid: "nav-partner", adminOnly: true },
];

export default function Layout({ children, fullBleed = false, headerExtra = null }) {
  const { user, logout, can } = useAuth();
  const { isMobile, toggle: toggleViewMode } = useViewMode();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("btheme") || "dark");
  const [maintWarn, setMaintWarn] = useState(0);   // P0.3.1 — badge (à échéance + en retard), une seule requête au montage
  // Compteur « À traiter » : nb de SITUATIONS en cours (épisodes ouverts, identité véhicule+type+règle) visibles
  // par l'utilisateur (RBAC serveur) — MÊME logique que la liste « À traiter » (1 épisode = 1 ligne = 1 situation).
  // null = inconnu (erreur de chargement) -> on n'affiche jamais 0 comme un résultat confirmé.
  const [triageCount, setTriageCount] = useState(null);

  useEffect(() => {
    api.get("/maintenance/overview?status=warn").then((r) => {
      const rows = r.data || [];
      setMaintWarn(rows.filter((x) => x.state === "due" || x.state === "overdue").length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/alert-episodes?status=open&limit=200")
      .then((r) => { if (!alive) return; setTriageCount((r.data || []).length); })
      .catch(() => { if (alive) setTriageCount(null); });
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("btheme", theme);
  }, [theme]);

  const links = nav.filter((n) => (!n.adminOnly || user?.role === "admin") && (!n.feature || can(n.feature) || (n.viewRoles || []).includes(user?.role)));

  const stopImpersonation = async () => {
    try {
      const res = await api.post("/admin/stop-impersonate");
    } catch {}
    localStorage.removeItem("impersonator_name");
    window.location.href = "/admin";
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
      isActive ? "bg-[#17A2B8]/15 text-[#2ec5db] border border-[#17A2B8]/30" : "text-muted-foreground hover:text-foreground hover:bg-black/5"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top banner (bandeau) */}
      <header className="fixed top-0 left-0 right-0 h-16 z-[1200] bg-card border-b border-border flex items-center gap-3 px-4 lg:px-6" data-testid="top-banner">
        <img src={logo} alt="Boomerang" className="h-8 w-auto object-contain cursor-pointer shrink-0" onClick={() => navigate("/dashboard")} data-testid="brand-logo" />

        {!isMobile && (
        <nav className="flex items-center gap-1 min-w-0 overflow-x-auto no-scrollbar">
          {links.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} className={linkClass}>
              <n.icon className="w-[18px] h-[18px]" /> {n.label}
              {n.to === "/maintenance" && maintWarn > 0 && <span data-testid="nav-maintenance-badge" className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#EF4444] text-white">{maintWarn}</span>}
              {n.to === "/alertes" && triageCount > 0 && <span data-testid="nav-alerts-badge" title="Situations à traiter" className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F5821F] text-white">{triageCount}</span>}
            </NavLink>
          ))}
        </nav>
        )}

        {!isMobile && <div className="flex flex-1 justify-end overflow-hidden pr-1">{headerExtra}</div>}

        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button onClick={toggleViewMode} data-testid="view-mode-toggle" title={isMobile ? "Passer en vue Bureau (complète)" : "Passer en vue Mobile (allégée)"} className="text-muted-foreground hover:text-[#F5821F] p-2 rounded-md hover:bg-black/5 transition-colors">
            {isMobile ? <Monitor className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
          </button>
          <button onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} data-testid="theme-toggle" title="Basculer thème clair/sombre" className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-black/5 transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <NotificationBell />
          <button onClick={() => navigate("/parametres")} data-testid="nav-settings" title="Paramètres" className="text-muted-foreground hover:text-[#17A2B8] p-2 rounded-md hover:bg-black/5 transition-colors">
            <SlidersHorizontal className="w-4 h-4" />
          </button>
          {!isMobile && (
          <div className="flex items-center gap-2 pl-2 border-l border-border">
            <div className="w-8 h-8 rounded-full bg-[#17A2B8] flex items-center justify-center text-xs font-bold shrink-0">{user?.name?.[0] || "U"}</div>
            <div className="min-w-0 hidden xl:block">
              <p className="text-xs font-semibold truncate max-w-[130px]">{user?.name}</p>
              <p className="text-[10px] flex items-center gap-1 text-muted-foreground uppercase tracking-wider"><ShieldCheck className="w-3 h-3 brand-orange" /> {user?.role}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" title="Déconnexion" className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-destructive/20 transition-colors"><LogOut className="w-4 h-4" /></button>
          </div>
          )}
        </div>
      </header>

      {/* Mobile dropdown */}
      {isMobile && open && (
        <>
          <div className="fixed inset-x-0 top-16 bottom-16 bg-black/60 z-[1150]" onClick={() => setOpen(false)} />
          <div className="fixed top-16 left-0 right-0 z-[1200] bg-card border-b border-border p-3 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto" data-testid="mobile-nav">
            {links.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`${n.testid}-m`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-[#17A2B8]/15 text-[#2ec5db]" : "text-muted-foreground hover:text-foreground hover:bg-black/5"}`}>
                <n.icon className="w-[18px] h-[18px]" /> {n.label}
                {n.to === "/maintenance" && maintWarn > 0 && <span data-testid="nav-maintenance-badge-m" className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#EF4444] text-white">{maintWarn}</span>}
              </NavLink>
            ))}
            <NavLink to="/parametres" onClick={() => setOpen(false)} data-testid="nav-settings-m"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-[#17A2B8]/15 text-[#2ec5db]" : "text-muted-foreground hover:text-foreground hover:bg-black/5"}`}>
              <SlidersHorizontal className="w-[18px] h-[18px]" /> Paramètres
            </NavLink>
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
              <div className="flex items-center gap-2 px-2">
                <div className="w-8 h-8 rounded-full bg-[#17A2B8] flex items-center justify-center text-xs font-bold">{user?.name?.[0] || "U"}</div>
                <div><p className="text-xs font-semibold">{user?.name}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role}</p></div>
              </div>
              <button onClick={logout} data-testid="logout-btn-m" className="text-muted-foreground hover:text-foreground p-2 flex items-center gap-1 text-sm"><LogOut className="w-4 h-4" /> Déconnexion</button>
            </div>
          </div>
        </>
      )}

      <main className={`pt-16 ${fullBleed ? "" : isMobile ? "pb-16" : "pb-0"}`}>
        <div className={fullBleed ? "" : isMobile ? "p-4" : "p-8"}>{children}</div>
      </main>

      {isMobile && <MobileTabBar onMenu={() => setOpen((o) => !o)} onCloseMenu={() => setOpen(false)} menuOpen={open} />}

      {user?.impersonated_by && (
        <div className={`fixed z-[1300] left-1/2 -translate-x-1/2 ${isMobile ? "bottom-[84px]" : "bottom-4"} flex items-center gap-2 bg-[#F5821F] text-black font-semibold rounded-full px-4 py-2 shadow-xl text-xs whitespace-nowrap`} data-testid="impersonation-banner">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span className="truncate max-w-[45vw]">Vous consultez le compte de {user.name}</span>
          <button onClick={stopImpersonation} data-testid="stop-impersonation-btn" className="ml-1 bg-black text-white rounded-full px-2.5 py-1 hover:bg-black/80 transition-colors">Revenir</button>
        </div>
      )}

      {ASSISTANT_ENABLED && user?.role === "admin" && <AssistantWidget />}
      {!user?.impersonated_by && <ChangePasswordForcedModal />}
    </div>
  );
}
